import React from 'react';
import { Box, Container, Link, List, ListItem, ListItemText, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import useThemeStyles from '../../theme/useThemeStyles';

const SUPPORT_EMAIL = 'getstarted@devignstudios.co.uk';

/**
 * Public account-deletion instructions for Google Play / App Store listing requirements.
 * Must stay reachable without login.
 */
function DeleteAccountPage() {
  const styles = useThemeStyles();

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography component="h1" className="pagename" sx={styles.text?.pagename}>
          Delete your Idotoo account
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          This page explains how to delete your Idotoo account and what happens to your data.
          Idotoo is the wedding planning app from Devign Studios.
        </Typography>

        <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
          Couples — delete in the app or website
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          The quickest way is to delete your account yourself while signed in:
        </Typography>
        <List dense sx={{ listStyleType: 'decimal', pl: 3, mb: 2, '& .MuiListItem-root': { display: 'list-item', py: 0.25 } }}>
          <ListItem disableGutters>
            <ListItemText primary="Sign in to Idotoo (app or website)." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary={
                <>
                  Go to{' '}
                  <Link component={RouterLink} to="/settings">
                    Account settings
                  </Link>
                  {' '}(/settings).
                </>
              }
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="In the Danger Zone section, tap or click Delete account." />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText primary="Type DELETE to confirm, then confirm deletion." />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you are not signed in, start at{' '}
          <Link component={RouterLink} to="/couple/login">
            couple login
          </Link>
          {' '}or{' '}
          <Link component={RouterLink} to="/login">
            login
          </Link>
          , then open settings.
        </Typography>

        <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
          Request deletion by email
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          Prefer not to use the in-app flow, or need help as a supplier? Email us from the address on your Idotoo account:
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Link href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Delete my Idotoo account')}`}>
            {SUPPORT_EMAIL}
          </Link>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Use the subject line “Delete my Idotoo account”. We will verify it is you, then process the request.
          Suppliers should use this email option (in-app supplier deletion is handled via support).
        </Typography>

        <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
          What we delete
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          After a confirmed request, we delete your account profile and associated wedding planning data
          linked to your account (including messages and uploaded files held for that account), usually within
          30 days.
        </Typography>

        <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
          What we may keep
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We may retain limited records where we must for legal, tax, fraud prevention, accounting, or
          dispute resolution, for as long as those requirements apply. More detail is in our{' '}
          <Link component={RouterLink} to="/privacy-policy">
            Privacy Policy
          </Link>
          .
        </Typography>

        <Typography component="h2" variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
          Delete some data without deleting your account
        </Typography>
        <Typography variant="body1">
          You can update or remove much of your information in the app while signed in. To request deletion
          of specific data without closing the account, email{' '}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
          {' '}and tell us what you want removed.
        </Typography>
      </Paper>
    </Container>
  );
}

export default DeleteAccountPage;
